import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import StorefrontSignInRoute from "storefronts/StorefrontSignInRoute";
import StorefrontRegisterRoute from "storefronts/StorefrontRegisterRoute";
import RegisterCustomer from "pages/customer/RegisterCustomer";
import PostLoginMobile from "mobile/pages/auth/PostLoginMobile";
import MobilePage from "mobile/MobilePage";
import SignOut from "components/SignOut";
import MobileDashboard from "mobile/pages/dashboard/MobileDashboard";
import SearchMobile from "mobile/pages/search/SearchMobile";
import PipelineMobile from "mobile/pages/pipeline/PipelineMobile";
import WorkOrderBuildMobile from "mobile/pages/pipeline/WorkOrderBuildMobile";
import WorkOrdersListMobile from "mobile/pages/workorders/WorkOrdersListMobile";
import WorkOrderDetailMobile from "mobile/pages/workorders/WorkOrderDetailMobile";
import InventoryMobile from "mobile/pages/inventory/InventoryMobile";
import InventoryDetailMobile from "mobile/pages/inventory/InventoryDetailMobile";
import ValveBodiesMobile from "mobile/pages/valvebodies/ValveBodiesMobile";
import ValveBodyDetailMobile from "mobile/pages/valvebodies/ValveBodyDetailMobile";
import SalesMobile from "mobile/pages/sales/SalesMobile";
import SalesOrderDetailMobile from "mobile/pages/sales/SalesOrderDetailMobile";
import CoreReturnsMobile from "mobile/pages/sales/CoreReturnsMobile";
import PullOrdersMobile from "mobile/pages/sales/PullOrdersMobile";
import PullOrderWorkflowMobile from "mobile/pages/sales/PullOrderWorkflowMobile";
import { WarrantyClaimChatMobile, WarrantyClaimDetailMobile, WarrantyClaimsMobile } from "mobile/pages/sales/WarrantyClaimsMobile";
import InvoicesMobile from "mobile/pages/invoices/InvoicesMobile";
import InvoiceDetailMobile from "mobile/pages/invoices/InvoiceDetailMobile";
import PaymentsMobile from "mobile/pages/payments/PaymentsMobile";
import PaymentDetailMobile from "mobile/pages/payments/PaymentDetailMobile";
import PurchaseOrdersMobile from "mobile/pages/purchase/PurchaseOrdersMobile";
import PurchaseOrderDetailMobile from "mobile/pages/purchase/PurchaseOrderDetailMobile";
import WarehouseReceivingMobile from "mobile/pages/warehouse/WarehouseReceivingMobile";
import WarehouseReceivingDetailMobile from "mobile/pages/warehouse/WarehouseReceivingDetailMobile";
import WarehouseShippingMobile from "mobile/pages/warehouse/WarehouseShippingMobile";
import CustomersMobile from "mobile/pages/customers/CustomersMobile";
import CustomerDetailMobile from "mobile/pages/customers/CustomerDetailMobile";
import VendorsMobile from "mobile/pages/vendors/VendorsMobile";
import VendorDetailMobile from "mobile/pages/vendors/VendorDetailMobile";
import ProjectionsMobile from "mobile/pages/projections/ProjectionsMobile";
import ImageGalleryMobile from "mobile/pages/gallery/ImageGalleryMobile";
import SettingsMobile from "mobile/pages/settings/SettingsMobile";
import SecureRoute from "components/SecureRoute";
import { mobileTheme } from "mobile/theme";
import { MobileLocaleProvider } from "mobile/i18n";
import MobileShell from "mobile/components/MobileShell";

function MobileRestricted() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fbfbfa", px: 2, py: 4 }}>
      <Typography variant="h5" fontWeight={900} gutterBottom>
        Restricted
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Your account does not have access to this workflow.
      </Typography>
    </Box>
  );
}

export default function MobileRoot() {
  return (
    <MobileLocaleProvider>
      <ThemeProvider theme={mobileTheme}>
        <MobileShell>
          <Routes>
            <Route path="/" element={<MobilePage page="home" />} />
          <Route path="/download" element={<MobilePage page="download" />} />
          <Route path="/qr" element={<MobilePage page="qrDeal" />} />
          <Route path="/deal" element={<MobilePage page="qrDeal" />} />
          <Route path="/scan" element={<MobilePage page="qrScan" />} />
          <Route path="/catalog" element={<MobilePage page="catalog" />} />
          <Route
            path="/catalog/:documentId"
            element={<MobilePage page="productDetail" />}
          />
          <Route path="/cart" element={<MobilePage page="cart" />} />
          <Route
            path="/checkout"
            element={
              <SecureRoute>
                <MobilePage page="checkout" />
              </SecureRoute>
            }
          />
          <Route path="/register" element={<StorefrontRegisterRoute />} />
          <Route path="/distributor/apply" element={<RegisterCustomer />} />
          <Route path="/partners/apply" element={<RegisterCustomer />} />
          <Route path="/authentication/sign-in" element={<StorefrontSignInRoute />} />
          <Route path="/sign-out" element={<SignOut />} />
          <Route
            path="/dashboard"
            element={
              <SecureRoute permissionKey="dashboard">
                <MobileDashboard />
              </SecureRoute>
            }
          />
          <Route
            path="/auth-dashboard"
            element={
              <SecureRoute permissionKey="auth-dashboard">
                <MobileDashboard />
              </SecureRoute>
            }
          />
          <Route
            path="/search"
            element={
              <SecureRoute>
                <SearchMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/pipeline"
            element={
              <SecureRoute permissionKey="pipeline">
                <PipelineMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/pipeline/work-orders/:documentId"
            element={
              <SecureRoute permissionKey="pipeline">
                <WorkOrderBuildMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/work-orders"
            element={
              <SecureRoute permissionKey="work-orders">
                <WorkOrdersListMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/work-orders/:documentId"
            element={
              <SecureRoute permissionKey="work-orders">
                <WorkOrderDetailMobile />
              </SecureRoute>
            }
          />
          <Route path="/build-sheets" element={<Navigate to="/m/dashboard" replace />} />
          <Route path="/build-sheets/:documentId" element={<Navigate to="/m/dashboard" replace />} />
          <Route
            path="/inventory"
            element={
              <SecureRoute permissionKey="inventory">
                <InventoryMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/inventory/:documentId"
            element={
              <SecureRoute permissionKey="inventory">
                <InventoryDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/valve-bodies"
            element={
              <SecureRoute permissionKey="valve-bodies">
                <ValveBodiesMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/internal/valve-bodies/:documentId"
            element={
              <SecureRoute permissionKey="valve-bodies">
                <ValveBodyDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/valve-bodies/:documentId"
            element={
              <SecureRoute permissionKey="valve-bodies">
                <ValveBodyDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <SecureRoute permissionKey="sales">
                <SalesMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/sales/:documentId"
            element={
              <SecureRoute permissionKey="sales">
                <SalesOrderDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/pull-orders"
            element={
              <SecureRoute permissionKey="sales">
                <PullOrdersMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/pull-orders/:documentId"
            element={
              <SecureRoute permissionKey="sales">
                <PullOrderWorkflowMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/core-returns"
            element={
              <SecureRoute permissionKey="sales">
                <CoreReturnsMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/sales/warranty-claims"
            element={
              <SecureRoute permissionKey="sales">
                <WarrantyClaimsMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/sales/warranty-claims/:documentId"
            element={
              <SecureRoute permissionKey="sales">
                <WarrantyClaimDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/sales/warranty-claims/:documentId/chat"
            element={
              <SecureRoute permissionKey="sales">
                <WarrantyClaimChatMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <SecureRoute permissionKey="invoices">
                <InvoicesMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/invoices/:documentId"
            element={
              <SecureRoute permissionKey="invoices">
                <InvoiceDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <SecureRoute permissionKey="payments">
                <PaymentsMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/payments/:documentId"
            element={
              <SecureRoute permissionKey="payments">
                <PaymentDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <SecureRoute permissionKey="purchase-orders">
                <PurchaseOrdersMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/purchase-orders/:documentId"
            element={
              <SecureRoute permissionKey="purchase-orders">
                <PurchaseOrderDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/warehouse/receiving"
            element={
              <SecureRoute permissionKey="warehouse-receiving">
                <WarehouseReceivingMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/warehouse/receiving/:documentId"
            element={
              <SecureRoute permissionKey="warehouse-receiving">
                <WarehouseReceivingDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/warehouse/shipping"
            element={
              <SecureRoute permissionKey="sales">
                <WarehouseShippingMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <SecureRoute permissionKey="customers">
                <CustomersMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/customers/:documentId"
            element={
              <SecureRoute permissionKey="customers">
                <CustomerDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <SecureRoute permissionKey="vendors">
                <VendorsMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/vendors/:documentId"
            element={
              <SecureRoute permissionKey="vendors">
                <VendorDetailMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/projections"
            element={
              <SecureRoute permissionKey="projections">
                <ProjectionsMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/media/gallery"
            element={
              <SecureRoute permissionKey="image-gallery">
                <ImageGalleryMobile />
              </SecureRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <SecureRoute>
                <SettingsMobile />
              </SecureRoute>
            }
          />
          <Route path="/restricted" element={<MobileRestricted />} />
          <Route path="/account" element={<MobilePage page="portalHome" />} />
          <Route path="/account/orders" element={<MobilePage page="portalOrders" />} />
          <Route path="/account/orders/:documentId" element={<MobilePage page="portalOrderDetail" />} />
          <Route path="/account/distributor-requests" element={<MobilePage page="portalDistributorRequests" />} />
          <Route path="/account/distributor-requests/:documentId" element={<MobilePage page="portalDistributorRequestDetail" />} />
          <Route path="/account/distributor-customers" element={<MobilePage page="portalDistributorCustomers" />} />
          <Route path="/account/valve-bodies" element={<MobilePage page="portalValveBodies" />} />
          <Route path="/account/core-returns" element={<MobilePage page="portalCoreReturns" />} />
          <Route path="/account/valve-bodies/:documentId" element={<MobilePage page="portalValveBodyDetail" />} />
          <Route path="/account/valve-bodies/:documentId/core-return" element={<MobilePage page="portalCoreReturn" />} />
          <Route path="/account/valve-bodies/:documentId/warranty" element={<MobilePage page="portalWarrantyCreate" />} />
          <Route path="/account/warranty-claims/:documentId" element={<MobilePage page="portalWarrantyClaim" />} />
          <Route path="/account/warranty-claims/:documentId/chat" element={<MobilePage page="portalWarrantyChat" />} />
          <Route path="/account/support" element={<MobilePage page="portalSupport" />} />
          <Route path="/account/support/new" element={<MobilePage page="portalSupportCompose" />} />
          <Route path="/account/support/:documentId" element={<MobilePage page="portalSupportThread" />} />
          <Route path="/account/profile" element={<MobilePage page="portalProfile" />} />
          <Route path="/account/addresses" element={<MobilePage page="portalAddresses" />} />
          <Route path="/post-login" element={<PostLoginMobile />} />
          <Route path="/dashboard-home" element={<Navigate to="/m/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/m" replace />} />
          </Routes>
        </MobileShell>
      </ThemeProvider>
    </MobileLocaleProvider>
  );
}
